import webglHome from 'config/webgl-home';
import HomeZonesConfig from 'app/views/home/config/HomeZonesConfig';
import LogoPixelCache from 'app/data/HomePixelCache/LogoPixelCache';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { resolveIconPixels } from 'app/views/home/webgl/particlesOptimised/iconDensity';
import { globals } from 'config/globals';
import { Events } from 'core/events/Events';
import { Simple } from 'utils/analytics';

import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import { ParticlesStage } from 'app/views/home/webgl/particles/scenes/ParticlesStage';
import {
	ParticleCore,
	BEHAVIOUR_PATH_ICON,
	BEHAVIOUR_PATH_ORBIT
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import {
	ParticleContainerRenderer,
	SplitBlendParticleRenderer
} from 'app/views/home/webgl/particlesOptimised/core/PoolRenderer';
import { FRAME_NAMES } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { getPathLookups } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';
import { ScriptList } from 'app/views/home/webgl/particlesOptimised/core/scripts';
import { progressParallaxKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/progressParallax';
import { mouseParallaxKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/mouseParallax';
import { pathKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/path';
import { physicsKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/physics';
import { repellKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/repell';
import { integrateKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/integrate';
import { breatheKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/breathe';
import IconDepartment from 'app/views/home/webgl/particlesOptimised/departments/IconDepartment';
import IntroDepartment from 'app/views/home/webgl/particlesOptimised/departments/IntroDepartment';
import ScreensDepartment from 'app/views/home/webgl/particlesOptimised/departments/ScreensDepartment';
import SpacesDepartment from 'app/views/home/webgl/particlesOptimised/departments/SpacesDepartment';
import SystemsDepartment from 'app/views/home/webgl/particlesOptimised/departments/SystemsDepartment';

import { DepartmentType } from 'app/data/Enums';

import type { Point } from 'app/contracts/Geometry';
import type { particles } from 'app/contracts/Particles';
import type { PoolName } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { PoolRenderer } from 'app/views/home/webgl/particlesOptimised/core/PoolRenderer';
import type { PhysicsFrame } from 'app/views/home/webgl/particlesOptimised/core/kernels/physics';
import type {
	DepartmentDeps,
	HomeTickContext,
	SystemsDepartmentLike
} from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';
import type {
	HomeItemGeometry,
	HomeStageSlots,
	HomeWebGL,
	HomeWebGLDeps,
	HomeWebGLFrame,
	HomeWebGLSizes,
	HomeWebGLType
} from 'app/views/home/webgl/webgl.types';

/**
 * ParticlesOptimisedWebGL — the 'particles-optimised' home WebGL type
 * (?webgl=particles-optimised): the SoA rewrite of the particle hot loop.
 *
 * STAGE 7: the work-section nav matrix is complete. setupForWork /
 * showFromWork are the verbatim HomeField routings (setup: SCREENS -> the
 * screens department's timer park, everything else — SYSTEMS included, a
 * reference quirk — to the spaces no-op; show: screens/spaces to their
 * departments, the else branch to systems), each department's showFromWork
 * being its full stop + restart. resetFromWork / deeplink /
 * updateWorkTransitionParticles / reset / resetIntervalsAndAnimations are
 * documented RUNTIME NO-OPS matching the reference — the back-from-work
 * drop-in is entirely item/DOM-side (TimelineDOMTransitions), deeplinks
 * land departments through the band switcher, and the whole
 * TransitionParticles apparatus is dead machinery over an empty pool (see
 * each method's note + docs/DECISIONS.md). freeze() now skips the sim but
 * keeps the HomeScreens redraw + container fades running (the reference
 * pauseForMenu split); destroy() clears department timers and the guides
 * mount. The spaces/systems phase lists moved to
 * departments/{spaces,systems}/*PhaseEntries.ts (owner-requested uniform
 * "where is the show" files).
 *
 * STAGE 6: the SPACES and SYSTEMS departments are REAL — the last two pools
 * leave demo mode, and all four drag bands drive live departments through
 * the switcher. Highlights:
 *
 *  - SPACES: the full box stage machine (rain / lady pixel image / square
 *    paths / random lerp lines / three sine grids / two organic-lines
 *    follow-swarm phases), the per-tick spaces mask redraw and the
 *    ReflectionFilter on stage.spacesOutter. The spaces pool renderer moved
 *    INSIDE stage.subContainers.spaces (masked by stage.spacesMask — a
 *    render-tree sibling wired by the reused ParticlesStage) and is a
 *    SplitBlendParticleRenderer (spaces stages flip per-sprite blendMode
 *    'add' exactly like the screens phases). The sine stages run through
 *    the dedicated sine kernel — the drag-profile hotspot this type was
 *    started for.
 *  - SYSTEMS: the reused ParticleGridTargets dot grid (guide container wired
 *    in create(), invalidated on resize like ParticlesWebGL), the travelling
 *    dotted lines with endpoint markers (OptimisedSingleLine over a lane
 *    free-list — the ParticlePool fork) and the rail-dot trails as lanes.
 *    The systems department also ticks while NOT active (the reference
 *    HomeField.tick guard) so the dot grid keeps tracking the drag.
 *  - Physics onTargetReached is now wired: the kernel's lerp-completion
 *    callback routes by lane range to the spaces/systems departments (the
 *    reference's per-particle closures). The physTargetSlot/targetsPx
 *    machinery stays UNFED — its only reference feeder is the systems
 *    department's unreachable Rain stage (dead code; kernel path is
 *    parity-proven and ready).
 *  - ParticleDepth stays UNWIRED on purpose: a stage-6 audit found every
 *    `depth.active = true` call sits in unreachable reference stages
 *    (spaces PerspectiveGrid/PerspectiveOrbit/LineDrop + the systems
 *    equivalents) — no LIVE home department enables it, so nothing sets
 *    BEHAVIOUR_DEPTH and the depth kernel is not in the frame loop (it is
 *    ported + parity-proven for when a stage list ever grows).
 *  - The switcher now dispatches CHANGE_HOME_SCENE + the start_home_scene
 *    analytics on scene switches (reference DepartmentSwitcher._switchScene
 *    — HomeController's drag-prompt reset and work-department tracking
 *    listen to it). The reference switcher's _updateSceneTransition
 *    progress.spaces/home/screens writes have NO readers anywhere, and its
 *    transition-share machinery moves 0 particles (transitionParticles = 0)
 *    — stage-7 verdict: dead with the rest of TransitionParticles,
 *    documented-not-ported (see updateWorkTransitionParticles).
 *  - The reference's SpacesGridField ('spaces-particle-field', count 1) is
 *    DEAD VISUALS: its single HomeParticle is constructed inactive
 *    (invisible) and nothing ever activates it; its colourise() has no
 *    caller. Documented-not-ported.
 *
 * STAGE 5: the SCREENS department is REAL — the drop machine (hit-tested
 * rain against the TV shapes), the radial halftone person and the swirl,
 * driven by the physics kernel (velocity/gravity + ParticleHitTests
 * readback), the extended repell kernel (POSITION/VECTOR) and the integrate
 * kernel's vector-movement mode. The screens pool renderer sits INSIDE
 * stage.subContainers.screens — the container HomeScreens' mask Graphics
 * clips (mask wiring built by the reused ParticlesStage; drawMask runs from
 * HomeScreens.tick, called here every frame like ParticlesWebGL does) — and
 * is a SplitBlendParticleRenderer because the reference flips per-sprite
 * blendMode between phases.
 *
 * STAGE 4: the icon + intro departments are REAL — the intro's orbiting
 * particle cloud (click-the-button choreography: bloom, hover, disperse)
 * and the debug.io logo pixel grid, driven by the path/breathe kernels and
 * the sparse script list. Highlights:
 *
 *  - PRELOAD HANDSHAKE mirrors ParticlesWebGL's sequencing: the CONSTRUCTOR
 *    resolves the icon pixel grid (LogoPixelCache — the same static cache
 *    module the reference pushes through setIconPixelData) and fires
 *    reportPixelDataReady; create() builds the scene and fires
 *    reportSceneCreated; onReady is deferred a macrotask (the reference
 *    defers ~1.6s of staggered particle creation — this type builds
 *    synchronously, so only the ordering survives), followed by the intro
 *    scene start after revealDelaysMs.sceneStart. The stage-1 synchronous
 *    stub is gone.
 *  - Departments own their pools: intro/icon implement DepartmentScene over
 *    core lanes; the facade routes tick (via the shared HomeTickContext) and
 *    the intro handshake to them. The reference's DepartmentSwitcher
 *    (drag-progress scene swapping into screens/spaces/systems) arrives with
 *    those departments in later stages — until then icon simply stays active
 *    after introComplete, exactly like the reference at home progress.
 *  - Kernel order per pool matches the reference behaviour add-order:
 *    progressParallax -> mouseParallax -> path(orbit) -> path(icon) ->
 *    repell -> integrate (ParticleProperties.tick) -> breathe (_setScale's
 *    breathing, which the reference runs after the scale inertia tick).
 *    The script list ticks once per frame at choreography level, before the
 *    kernels (counter semantics — see core/scripts.ts).
 *  - Pool mounting quirk kept: reference intro particles live in the ROOT
 *    particle container (HomeField's particleFactory has no intro range
 *    check), NOT the icon container — so the intro renderer mounts on this
 *    type's root container, above the stage sub-tree. Icon mounts inside
 *    stage.iconContainer, which the active department positions per tick
 *    (containerX + zonePositions.homeParticles.cx * virtualWidth).
 *  - Both intro and icon renderers run blendMode 'add' container-wide (the
 *    reference sets sprite.blendMode = 'add' on every particle of both
 *    pools).
 *
 * Two hard-won constraints honoured here (see docs/DECISIONS.md):
 *  - never set cacheAsBitmap/cacheAsTexture (8.6+ re-activated the v5 relic
 *    and snapshots hidden containers black);
 *  - boot verification must measure PIXELS, not just console silence;
 *  - globals.slowFpsMode SKIPS ReflectionFilter creation and headless
 *    SwiftShader TRIPS it — spaces filter verification must run HEADED.
 */

export class ParticlesOptimisedWebGL implements HomeWebGL {
	public readonly type: HomeWebGLType = 'particles-optimised';

	private _deps: HomeWebGLDeps;
	private _container: PIXI.Container;
	private _guideContainer: PIXI.Container;
	private _stage: ParticlesStage;
	private _core: ParticleCore;
	private _renderers: Partial<Record<PoolName, PoolRenderer>> = {};
	private _sizes: HomeWebGLSizes;
	private _geometry: HomeItemGeometry;
	private _sceneProps: particles.SharedSceneContext;

	private _scripts: ScriptList;
	private _context: HomeTickContext = { containerX: 0, progress: 0.5, homeContainerX: 0, deltaTime: 1 };
	private _introDepartment: IntroDepartment;
	private _iconDepartment: IconDepartment;
	private _screensDepartment: ScreensDepartment;
	private _spacesDepartment: SpacesDepartment;
	//Typed as the interface (not the concrete SystemsDepartment) so the
	//'shaders' subclass can substitute a GPU systems department via
	//_createSystemsDepartment — see that hook + docs/DESIGN-shaders-type.md §8:
	private _systemsDepartment: SystemsDepartmentLike;
	private _activeDepartment: DepartmentScene = null;

	//DepartmentSwitcher's per-scene start counts (start_home_scene analytics):
	private _sceneStartCounts: Record<string, number> = { icon: 0, systems: 0, screens: 0, spaces: 0 };

	//ParticleBehaviourLookup.pause() equivalent (see DepartmentDeps.
	//setKernelsPaused): kernels / scripts / uploads skip while set:
	private _kernelsPaused = false;

	//Per-frame physics inputs (persistent object, mutated once per tick):
	private _physicsFrame: PhysicsFrame = {
		deltaTime: 1,
		containerW: 0,
		containerH: 0,
		containerX: 0,
		progress: 0.5,
		boundaryY: 0,
		boundaryW: 0,
		boundaryH: 0,
		screensContainerX: 0,
		targetsPx: [],
		onTargetReached: null
	};

	//The DepartmentSwitcher fork (see _checkSwitchScene): drag-progress bands
	//select the active scene once the intro completes. Scenes without a
	//department yet (screens/spaces/systems) switch to "nothing active" —
	//the load-bearing part for stage 4 is that LEAVING them re-enters 'icon'
	//through the full startAsActiveScene re-formation:
	private _switcherActive = false;
	private _activeSceneName: string = 'icon';
	private _pendingSceneStart: string = null;

	private _frozen = false;
	private _iconInitDone = false;
	private _startupTimeouts: Array<ReturnType<typeof setTimeout>> = [];

	constructor(deps: HomeWebGLDeps) {
		this._deps = deps;

		//The pixel-data half of the preload handshake, from the constructor
		//like the reference (ParticlesWebGL._loadPixelData fires
		//reportPixelDataReady synchronously after kicking off its loads; the
		//icon grid itself is LogoPixelCache — a static module, decoded at
		//import). The screens halftone image loads the reference also starts
		//here belong to the screens department (later stage):
		this._deps.reportPixelDataReady();
	}

	public create(
		slots: HomeStageSlots,
		spritesheet: PIXI.Spritesheet,
		sizes: HomeWebGLSizes,
		geometry: HomeItemGeometry
	): void {
		this._sizes = sizes;
		this._geometry = geometry;
		//The timeline's shared scene-state bag (read like ParticlesWebGL does;
		//the screens department also writes its particleContainers seam):
		this._sceneProps = this._deps.getSceneProps();

		//The stage builds its sub-container tree inside this container (same
		//shape as HomeField handing its particle container to ParticlesStage):
		this._container = new PIXI.Container();
		this._stage = new ParticlesStage(this._container, spritesheet.textures);
		//After the stage constructor: it names its host 'PARTICLES CONTAINER'
		//(reused verbatim) — relabel so tooling can tell the types apart:
		this._container.name = 'ParticlesOptimised';
		slots.webglMain.addChild(this._container);

		//Experimental ?iconCount=N dev knob (iconDensity.ts) — greatly densify the
		//intro logo to exercise the SoA headroom the S1-GATE weak-GPU read measured
		//(docs/PERF-shaders-scaling.md). OFF by default. Must land BEFORE the core
		//(which sizes the icon pool from HomeFieldConfig.iconParticles) and lift the
		//mobile cap (IconDepartment clamps the phone to iconParticlesMobile), or the
		//densified logo never reaches the device it is meant to be tested on. icon
		//is a middle pool slice, so the later departments just shift up in the
		//larger pool — their counts are unchanged.
		const iconPixels = resolveIconPixels();
		if (iconPixels.length !== LogoPixelCache.data.length) {
			HomeFieldConfig.iconParticles = iconPixels.length;
			HomeFieldConfig.iconParticlesMobile = iconPixels.length;
			HomeFieldConfig.indexesCache = null;
		}

		this._core = new ParticleCore();
		this._scripts = new ScriptList();

		const frames = FRAME_NAMES.map((name) => spritesheet.textures[name]);

		//One renderer per non-empty pool. Mount map mirrors the reference's
		//particleFactory container routing:
		// - intro: the ROOT container (reference quirk — intro indexes fall
		//   through the factory's range checks into the default container,
		//   added after the stage children, so intro draws on top), 'add';
		// - icon: the stage's icon container, 'add';
		// - screens (stage 5): INSIDE stage.subContainers.screens — the
		//   container masked by HomeScreens.maskGraphic (wired as a SIBLING in
		//   ParticlesStage), so the points clip to the lit TV shapes;
		// - spaces (stage 6): INSIDE stage.subContainers.spaces — masked by
		//   stage.spacesMask, which SpacesDepartment redraws every tick;
		// - systems: the systems container.
		//screens + spaces use the split-blend renderer (their phase machines
		//flip per-sprite blendMode 'add' <-> null).
		const mounts: Partial<Record<PoolName, PIXI.Container>> = {
			intro: this._container,
			icon: this._stage.iconContainer,
			screens: this._stage.subContainers.screens,
			spaces: this._stage.subContainers.spaces,
			systems: this._stage.systemsContainer
		};
		//The 'shaders' subclass runs systems on the GPU (its own textures +
		//render), so it drops the CPU systems pool renderer entirely — no
		//per-particle systems upload, and _tickSimulation runs no systems
		//kernels (the range is simply absent from _renderers). Non-behavioural
		//for the default type (_useGpuSystems() is false):
		if (this._useGpuSystems()) {
			delete mounts.systems;
		}
		const blendModes: Partial<Record<PoolName, string>> = {
			intro: 'add',
			icon: 'add'
		};

		(Object.keys(mounts) as Array<PoolName>).forEach((name) => {
			const range = this._core.pool(name);
			if (!range.count) {
				return;
			}
			const renderer: PoolRenderer =
				name === 'screens' || name === 'spaces'
					? new SplitBlendParticleRenderer(this._core, range, frames, `pop-${name}`)
					: new ParticleContainerRenderer(this._core, range, frames, `pop-${name}`, blendModes[name]);
			mounts[name].addChild(renderer.container);
			this._renderers[name] = renderer;
		});

		//Guide container for the systems dot grid (reference: ParticlesWebGL
		//create()) — ParticleGridTargets draws its Graphics inside it:
		this._guideContainer = new PIXI.Container();
		slots.guides.addChild(this._guideContainer);
		ParticleGridTargets.setGuideContainer(this._guideContainer);

		//Departments (they own the intro/icon/screens pools — see the class
		//header):
		const departmentDeps: DepartmentDeps = {
			core: this._core,
			scripts: this._scripts,
			stage: this._stage,
			getSizes: () => this._sizes,
			context: this._context,
			sceneProps: this._sceneProps,
			setKernelsPaused: (paused: boolean) => {
				this._kernelsPaused = paused;
			},
			colouriseSpacesItems: (colourSet, screenColour, floorColour) =>
				this._deps.colouriseSpacesItems(colourSet, screenColour, floorColour),
			colouriseSystemsItems: (colourSet, instant) => this._deps.colouriseSystemsItems(colourSet, instant)
		};
		this._introDepartment = new IntroDepartment(departmentDeps);
		this._iconDepartment = new IconDepartment(departmentDeps);
		this._iconDepartment.setIconPixelData(iconPixels);
		this._screensDepartment = new ScreensDepartment(departmentDeps);
		this._spacesDepartment = new SpacesDepartment(departmentDeps);
		this._systemsDepartment = this._createSystemsDepartment(departmentDeps);
		this._activeDepartment = this._introDepartment;

		//ParticlePhysics.onTargetReached — the kernel's lerp-completion fires
		//route by lane range (spaces chains / systems line heads):
		const spacesRange = this._core.pool('spaces');
		const systemsRange = this._core.pool('systems');
		this._physicsFrame.onTargetReached = (lane: number) => {
			if (lane >= spacesRange.start && lane < spacesRange.end) {
				this._spacesDepartment.onPhysicsTargetReached(lane);
			} else if (lane >= systemsRange.start && lane < systemsRange.end) {
				this._systemsDepartment.onPhysicsTargetReached(lane);
			}
		};

		//Scene-built half of the preload handshake (reference: end of
		//ParticlesWebGL.create()):
		this._deps.reportSceneCreated();

		//onReady + intro start, deferred off the create() call like the
		//reference's onParticlesReady continuation (whose ~1.6s of staggered
		//particle creation this type doesn't need — only the ordering and the
		//sceneStart delay survive):
		this._startupTimeouts.push(
			setTimeout(() => {
				//Screens-TV overlay wiring first (reference onParticlesReady
				//handler order: _initScreens precedes deps.onReady):
				this._initScreens();
				this._deps.onReady();

				this._startupTimeouts.push(
					setTimeout(() => {
						//HomeField.startIntroScene(): the intro department
						//becomes the active scene (no-op on skipIntroClick):
						this._activeDepartment = this._introDepartment;
						this._introDepartment.startAsActiveScene();
					}, webglHome.revealDelaysMs.sceneStart)
				);
			}, 0)
		);
	}

	public tick(frame: HomeWebGLFrame): void {
		if (!this._core || !this._sizes) {
			return;
		}

		const zones = this._sizes.zones;
		if (!zones || !zones.home) {
			return;
		}

		//freeze() split, mirroring the reference: ParticleBehaviourLookup.
		//pause + HomeField.freeze halt the particle sim completely, but
		//ParticlesWebGL.tick keeps running its shell every frame while
		//pauseForMenu holds the freeze — so here the sim (departments /
		//switcher / scripts / kernels / uploads) skips while the HomeScreens
		//mask redraw and the container fades below keep running:
		if (!this._frozen) {
			this._tickSimulation(frame, zones);
		}

		//Screens-TV overlay redraw (mask / darken / overlay positions off the
		//shared item sprites) — after the particle pass, like ParticlesWebGL
		//(gated internally on HomeScreens.isInit):
		HomeScreens.tick();

		//Config-driven container fades (same constants the particles type uses):
		const fades = webglHome.containerFades;
		this._stage.iconContainer.alpha = Math.max(0, Math.min(1, 1 - frame.progress / fades.iconFadeOutSpan));
		this._stage.systemsContainer.alpha = Math.max(
			0,
			Math.min(1, (frame.progress - fades.systemsFadeInStart) / fades.systemsFadeInSpan)
		);
	}

	/** The frozen-skippable half of tick() — see the freeze() note. */
	private _tickSimulation(frame: HomeWebGLFrame, zones: HomeWebGLSizes['zones']): void {
		//Per-frame scalars, computed ONCE outside the kernels (the design's
		//"S" struct). deltaTime is the same clamped channel the reference
		//Inertia reads (window.deltaTime, maintained by the app kernel):
		const containerW = this._sizes.virtualWidth;
		const containerH = this._sizes.innerHeight;
		const deltaTime = (window as any).deltaTime;
		//Reference sprite-write scale factor (ParticleProperties._setScale
		//multiplies by globals.scaleFactorM — set by HomeController on resize;
		//guard the pre-first-resize undefined):
		const scaleMultiplier = globals.scaleFactorM || 1;

		//Shared tick context for the departments (identity-stable — see
		//departments.types). homeContainerX is the reference departments'
		//_positionParticleContainer value (= particleContainersX.home):
		const homeParticles = this._sizes.zonePositions ? this._sizes.zonePositions.homeParticles : null;
		this._context.containerX = frame.containerX;
		this._context.progress = frame.progress;
		this._context.deltaTime = deltaTime;
		this._context.homeContainerX = homeParticles ? frame.containerX + homeParticles.cx * containerW : 0;

		//Icon pool's reference-constructor state needs the live containerX,
		//so it lands on the first ready tick (see IconDepartment.initialState):
		if (!this._iconInitDone) {
			this._iconInitDone = true;
			this._iconDepartment.initialState();
		}

		//Deferred scene start (the 10ms setActiveSceneProcessor hop) —
		//processed here so it can wait for zone readiness. Stale pends (the
		//band changed again inside the hop) are dropped, like the reference's
		//latest-wins DelayHelper chain:
		if (this._pendingSceneStart) {
			const name = this._pendingSceneStart;
			this._pendingSceneStart = null;

			if (name === this._activeSceneName) {
				const department = this._departmentByName(name);
				this._activeDepartment = department;
				if (department) {
					department.startAsActiveScene();
				}
			}
		}

		//DepartmentSwitcher._checkSwitchScene (reference lines 99-120): once
		//the main scene has started, drag progress selects the active scene
		//from the shared bands (null between bands = no switch). Leaving and
		//re-entering the icon band re-runs the FULL icon formation —
		//base-size grid, initIcon resets (expand target 1, hardSetScale 0.2)
		//and the delayed scale-up — which is what makes the D re-form
		//instead of staying terminally expanded:
		if (this._switcherActive) {
			const scene = HomeZonesConfig.getParticleSceneFromProgress(frame.progress);
			if (scene !== null && scene !== this._activeSceneName) {
				this._switchScene(scene);
			}
		}

		//Department choreography first, then the script list, then the
		//kernels — the reference order (fields tick before
		//ParticleBehaviourLookup.tickActive; counters are behaviours but
		//their observable writes are target-level, order-equivalent here).
		//The department tick keeps running while the kernels are paused —
		//exactly the reference split (HomeField.tick runs during
		//ParticleBehaviourLookup.pause, particle ticks don't):
		if (this._activeDepartment) {
			this._activeDepartment.tick();
		}

		//HomeField.tick's systems guard: the systems department ticks every
		//frame it is NOT the active scene (dot grid tracking + container
		//positioning + the boot colour pass). The _active flag — not scene
		//identity — gates it, reproducing the reference's brief double-tick
		//window during the systems start beat:
		if (this._systemsDepartment && !this._systemsDepartment.isActive) {
			this._systemsDepartment.tick();
		}

		if (!this._kernelsPaused) {
			this._scripts.tick(deltaTime);

			//Mouse scalars, resolved once (the reference's per-frame sceneProps
			//mouse writes, ParticlesWebGL lines 468-486):
			const parallaxX = frame.mouseCords.x / this._sizes.innerWidth - 0.5;
			const parallaxY = frame.mouseCords.y / this._sizes.innerHeight - 0.5;
			//Touch gate: ParticleMouseParallax.tick returns immediately on touch:
			const mouseParallaxActive = !globals.lastCordsFromTouch;
			//Per-pool repell centres — MouseTarget(zone): raw cords minus that
			//zone's particle container x/y (the departments write the X seam
			//every tick; the spaces/systems Y entries are 0-initialised and
			//never written, exactly like the reference). Icon targets homeMouse;
			//the intro pool's repell is never active (see IntroDepartment):
			const homeMouseX = frame.mouseCords.x - this._context.homeContainerX;
			const containersX = this._sceneProps.mouse.particleContainersX;
			const containersY = this._sceneProps.mouse.particleContainersY;
			const screensMouseX = frame.mouseCords.x - (containersX.screens || 0);
			const screensMouseY = frame.mouseCords.y - (containersY.screens || 0);
			const spacesMouseX = frame.mouseCords.x - (containersX.spaces || 0);
			const spacesMouseY = frame.mouseCords.y - (containersY.spaces || 0);
			const systemsMouseX = frame.mouseCords.x - (containersX.systems || 0);
			const systemsMouseY = frame.mouseCords.y - (containersY.systems || 0);

			//zones.spaces.scale — ParticlePath's scaleAffect multiplier (the
			//SPACES zone scale even on home orbits — reference quirk):
			const spacesZoneScale = zones.spaces ? zones.spaces.scale : 1;
			const lookups = getPathLookups();

			//Physics per-frame inputs, resolved once (the boundary is ALWAYS
			//zonePositions.box — see the physics kernel header):
			const box = this._sizes.zonePositions ? this._sizes.zonePositions.box : null;
			const physics = this._physicsFrame;
			physics.deltaTime = deltaTime;
			physics.containerW = containerW;
			physics.containerH = containerH;
			physics.containerX = frame.containerX;
			physics.progress = frame.progress;
			physics.boundaryY = box ? box.y : 0;
			physics.boundaryW = box ? box.w : 0;
			physics.boundaryH = box ? box.h : 0;
			physics.screensContainerX = containersX.screens || 0;

			//Kernel order is fixed — mirrors ParticleBehaviourManager's add-order
			//(HomeParticle: position, mouseParallax, orbitPath, iconPath,
			//[counters], depth, [hover], physics, repell) followed by
			//ParticleProperties.tick (integrate) and its _setScale breathing
			//pass. The depth kernel's slot sits between the paths and physics —
			//stage-6 audit: every reference `depth.active = true` lives in
			//UNREACHABLE stages, so no live department sets BEHAVIOUR_DEPTH and
			//the (ported, parity-proven) depth kernel stays out of the loop:
			(Object.keys(this._renderers) as Array<PoolName>).forEach((name) => {
				const range = this._core.pool(name);
				const repellCenterX =
					name === 'icon'
						? homeMouseX
						: name === 'screens'
							? screensMouseX
							: name === 'spaces'
								? spacesMouseX
								: name === 'systems'
									? systemsMouseX
									: frame.mouseCords.x;
				const repellCenterY =
					name === 'screens'
						? screensMouseY
						: name === 'spaces'
							? spacesMouseY
							: name === 'systems'
								? systemsMouseY
								: frame.mouseCords.y;

				progressParallaxKernel(this._core, range, containerW, containerH, frame.containerX, frame.progress);
				if (mouseParallaxActive) {
					mouseParallaxKernel(this._core, range, parallaxX, parallaxY, containerW, containerH, deltaTime);
				}
				pathKernel(this._core, this._core.orbitPath, range, BEHAVIOUR_PATH_ORBIT, lookups, deltaTime, spacesZoneScale);
				pathKernel(this._core, this._core.iconPath, range, BEHAVIOUR_PATH_ICON, lookups, deltaTime, spacesZoneScale);
				physicsKernel(this._core, range, physics);
				repellKernel(this._core, range, repellCenterX, repellCenterY, deltaTime);
				integrateKernel(this._core, range, deltaTime);
				breatheKernel(this._core, range, deltaTime);
				this._renderers[name].upload(scaleMultiplier);
			});
		}
	}

	public resize(sizes: HomeWebGLSizes, firstResize: boolean): void {
		this._sizes = sizes;

		//Invalidate the systems dot grid (reference: ParticlesWebGL.resize):
		ParticleGridTargets.resize(sizes.innerWidth, sizes.innerHeight);

		//The reference forwards resizeEvent to the ACTIVE scene on the first
		//resize only (ParticlesWebGL.resize):
		if (firstResize && this._activeDepartment) {
			this._activeDepartment.resize();
		}
	}

	/**
	 * ParticlesWebGL.freeze = ParticleBehaviourLookup.pause() +
	 * HomeField.freeze(): the particle sim halts completely (tickActive
	 * early-returns, the field's department/switcher/colour choreography
	 * stops) while the per-frame shell keeps running — pauseForMenu keeps
	 * HomeController ticking through a freeze. _frozen reproduces that split
	 * in tick(). Callers: HomeTimeline.freeze/unfreeze via
	 * HomeController.freeze (history-state jumps, scene hidden 500ms later)
	 * and pauseForMenu/unpause (nav menu).
	 */
	public freeze(): void {
		this._frozen = true;
	}

	public unfreeze(): void {
		this._frozen = false;
	}

	public show(): void {
		if (this._container) {
			this._container.visible = true;
		}
	}

	public hide(): void {
		if (this._container) {
			this._container.visible = false;
		}
	}

	/** Reference ParticlesWebGL.reset is a documented no-op — the timeline's
	 *  reset paths (resetIntervalsAndAnimations / resetForDeeplink) are pure
	 *  item/DOM loops on the shared side. Lane positions re-derive every
	 *  tick here. */
	public reset(): void {}

	/**
	 * The back-from-work drop-in: reference ParticlesWebGL.resetFromWork is a
	 * documented NO-OP — the animate-down-from-top is entirely the item/DOM
	 * half (TimelineDOMTransitions.resetFromWork computes and applies offY),
	 * and the particle scene re-forms through showFromWork. The switcher
	 * needs no reset either: _activeSceneName still holds the scene the user
	 * left home from (CHANGE_HOME_SCENE kept HomeController's
	 * _activeWorkDepartment in sync with it), and preActivateScenes'
	 * progress reset lands inside that band, so the resumed ticks see
	 * scene === active and do not re-switch.
	 */
	public resetFromWork(department: DepartmentType, offY: number): void {}

	/**
	 * Reference ParticlesWebGL.deeplink is a no-op, and the timeline only
	 * ever calls it with null (HomeTimeline.resetForDeeplink — the
	 * department arg exists only in the contract). Landing a department
	 * fully formed on hard loads (/dev/home/{dept}) needs no webgl-side work
	 * on either type: HomeController's _resetXProgress jump writes the new
	 * progress and runs one instant timeline tick, and the band switcher in
	 * tick() sees the progress inside the department's band and runs the
	 * full startAsActiveScene formation (probe-verified for all four bands,
	 * stage 7).
	 */
	public deeplink(department: DepartmentType | null): void {}

	/**
	 * ParticlesWebGL.setupForWork -> HomeField.setupForWork, verbatim
	 * routing: SCREENS parks the screens department's choreography; EVERY
	 * other department — including SYSTEMS (reference quirk) — routes to the
	 * spaces department, whose setupForWork is the BaseDepartmentScene
	 * no-op. The reference webgl layer does NOT hide its own container here
	 * ($homeContainer hides in HomeTimeline.setupForWork, the scene
	 * container in HomeController.deactivateForWork, which also stops the
	 * tick feed), so neither does this one. Call sequence: explore click ->
	 * HomeController.hide -> SiteTransitions.startHomeToWork -> t+1000ms
	 * deactivateForWork -> timeline.setupForWork -> here.
	 */
	public setupForWork(department: DepartmentType): void {
		if (department === DepartmentType.SCREENS) {
			this._screensDepartment.setupForWork();
		} else {
			this._spacesDepartment.setupForWork();
		}
	}

	/**
	 * ParticlesWebGL.showFromWork = manager.showFromWork (whose
	 * homeField.showAllButTransition body is a dead `return;` in
	 * ParticleField — skipped) + HomeField.showFromWork's routing, verbatim:
	 * SCREENS/SPACES to their departments, the else branch — SYSTEMS, and
	 * anything unexpected — to systems. Each department's showFromWork is
	 * its full stop + restart, so the scene re-forms as home slides back in.
	 * Call sequence: back click -> WorkController.hide -> SiteTransitions.
	 * startWorkToHome -> t+800ms preActivateScenes (unfreeze + progress
	 * reset to the department band + one instant tick) -> t+1600ms
	 * activateScenes -> timeline.showFromWork -> here.
	 */
	public showFromWork(department: DepartmentType): void {
		if (department === DepartmentType.SCREENS) {
			this._screensDepartment.showFromWork();
		} else if (department === DepartmentType.SPACES) {
			this._spacesDepartment.showFromWork();
		} else {
			this._systemsDepartment.showFromWork();
		}
	}

	/**
	 * Driven per animation frame by the work intro drag (WorkController ->
	 * HomeController -> HomeTimeline -> here). RUNTIME NO-OP matching the
	 * reference: ParticlesWebGL routes this to TransitionParticles.
	 * updateWorkTransitionParticles, which early-returns unless
	 * _canProcessDragUpdate is set — and that flag's only writers are
	 * TransitionParticles.setupForWork (no callers anywhere) and toWork
	 * (whose body opens with `return;`, and whose own caller chain —
	 * HomeField.animateToWork — has no callers either). The transition pool
	 * is EMPTY anyway (HomeFieldConfig.transitionParticles = 0, so
	 * particlesFromIndex(0, 0) = []), and its container holds only a
	 * never-drawn gridlines sprite — even the reachable container x/alpha
	 * writes would move nothing visible. Dead machinery,
	 * documented-not-ported (see docs/DECISIONS.md, stage 7).
	 */
	public updateWorkTransitionParticles(introXPx: number, introProgressRatio: number): void {}

	/** No swap half — reference intro starts from create()'s deferred
	 *  continuation (see the startup timeouts), like ParticlesWebGL. */
	public introStart(): void {}

	public introHover(): void {
		//ParticlesWebGL.introHover: OPENING_HOVER state (manager-side no-op
		//here) + homeField.introMouseOver:
		if (this._introDepartment) {
			this._introDepartment.mouseOver();
		}
	}

	public introOut(): void {
		if (this._introDepartment) {
			this._introDepartment.mouseOut();
		}
	}

	/** The intro-complete handoff (ParticlesWebGL.introComplete →
	 *  HomeField.introComplete + startMainScene): intro particles disperse,
	 *  the screens department parks (HomeField.startMainScene stops the
	 *  screens/spaces processors before the icon starts), then the icon
	 *  department becomes the active scene and forms the logo, the
	 *  progress-band scene switcher arms
	 *  (DepartmentSwitcher.startMainScene('icon')), and the screens TVs
	 *  reveal (HomeScreens.showAtStart + the glow item refs — the swap half
	 *  of the reference intro-complete). */
	public introComplete(): void {
		if (!this._introDepartment) {
			return;
		}

		this._introDepartment.introComplete();

		//HomeField.startMainScene's spaces + screens stop (that order). The
		//screens stop is a full reset — HomeScreens darkens back, hit tests
		//off, container hidden:
		this._spacesDepartment.stopAsActiveScene();
		this._screensDepartment.stopAsActiveScene();

		//setActiveSceneProcessor(icon): stop the outgoing scene now, start
		//the incoming one a beat later (reference: 10ms DelayHelper; the
		//skipNormalParticleUpdates one-frame skip is an object-pipeline hack
		//with no SoA equivalent needed). The start itself waits for zone
		//readiness in tick():
		this._introDepartment.stopAsActiveScene();
		this._activeDepartment = null;
		this._activeSceneName = 'icon';
		this._switcherActive = true;
		this._startupTimeouts.push(
			setTimeout(() => {
				this._pendingSceneStart = 'icon';
			}, 10)
		);

		//ParticlesWebGL.introComplete's screens-TV reveal + glow wiring:
		HomeScreens.showAtStart();
		HomeScreens.glows = this._geometry.getItems('screens-bg-blob-glow');
	}

	/** Region cut-overs (reference: HomeLogoArea -> iconHover, Home/Spaces ->
	 *  manager state changes). Every mapped call bottoms out in a no-op for
	 *  the departments built so far (changeIconColourOnHover is empty,
	 *  HomeField.changeState is empty); the routing exists so the remaining
	 *  departments plug into the same seam. */
	public onRegionEnter(id: string): void {}

	public onRegionExit(id: string): void {}

	public onExploreHover(center: Point): void {}

	public onExploreOut(department: DepartmentType): void {}

	/** Reference ParticlesWebGL.resetIntervalsAndAnimations is a documented
	 *  no-op (the item loop stays in the timeline; no swap-side intervals). */
	public resetIntervalsAndAnimations(): void {}

	/**
	 * Contract-optional teardown (no reference equivalent — ParticlesWebGL
	 * has no destroy, and nothing calls this in production; it exists for
	 * harness/type-swap hygiene). Stage-7 audit additions: the departments'
	 * pending native timers (resize debounces, the icon delayed scale-up)
	 * are cleared so nothing fires into the torn-down scene, and the systems
	 * dot-grid guide container is removed from the HOST's guides slot (it is
	 * the one thing this type mounts outside its own container). Known
	 * leftovers, accepted: DelayHelper/ComboCounter entries die with the
	 * tick feed; HomeScreens' module statics stay initialised (shared
	 * singleton — the next home boot re-inits them); the spaces
	 * ReflectionFilter/filter shape are destroyed with the container tree.
	 */
	public destroy(): void {
		this._startupTimeouts.forEach((handle) => clearTimeout(handle));
		this._startupTimeouts = [];

		if (this._scripts) {
			this._scripts.clear();
			this._scripts = null;
		}

		if (this._iconDepartment) {
			this._iconDepartment.destroy();
		}
		if (this._screensDepartment) {
			this._screensDepartment.destroy();
		}
		if (this._systemsDepartment) {
			this._systemsDepartment.destroy();
		}

		this._introDepartment = null;
		this._iconDepartment = null;
		this._screensDepartment = null;
		this._spacesDepartment = null;
		this._systemsDepartment = null;
		this._activeDepartment = null;

		(Object.keys(this._renderers) as Array<PoolName>).forEach((name) => {
			this._renderers[name].destroy();
		});
		this._renderers = {};
		if (this._guideContainer) {
			this._guideContainer.parent?.removeChild(this._guideContainer);
			this._guideContainer.destroy({ children: true });
			this._guideContainer = null;
		}
		if (this._container) {
			this._container.parent?.removeChild(this._container);
			this._container.destroy({ children: true });
			this._container = null;
		}
		this._core = null;
		this._stage = null;
	}

	//Backend seams (overridden by the 'shaders' subclass — see ShadersWebGL /
	//docs/DESIGN-shaders-type.md §8). Both are no-ops for this type.
	//-----------------------------------------------------------------------------------------------

	/** Whether the systems department runs on the GPU (design per-phase backend
	 *  selection). False here: systems is a normal CPU pool. When true, create()
	 *  drops the CPU systems pool renderer/kernels. */
	protected _useGpuSystems(): boolean {
		return false;
	}

	/** Factory for the systems department — the default CPU SystemsDepartment.
	 *  The 'shaders' subclass returns a GpuSystemsDepartment instead. */
	protected _createSystemsDepartment(deps: DepartmentDeps): SystemsDepartmentLike {
		return new SystemsDepartment(deps);
	}

	//Private
	//-----------------------------------------------------------------------------------------------

	/** Scene id -> department (all four bands are live as of stage 6). */
	private _departmentByName(name: string): DepartmentScene {
		if (name === 'icon') {
			return this._iconDepartment;
		}
		if (name === 'screens') {
			return this._screensDepartment;
		}
		if (name === 'spaces') {
			return this._spacesDepartment;
		}
		if (name === 'systems') {
			return this._systemsDepartment;
		}
		return null;
	}

	/**
	 * HomeScreens wiring (fork of ParticlesWebGL._initScreens): the shared
	 * TV-frame item sprites + the systems-block hit-test items. Runs from the
	 * deferred create() continuation, before deps.onReady() — the reference
	 * onParticlesReady order:
	 */
	private _initScreens(): void {
		HomeScreens.init(
			{
				topLeft: this._geometry.getItems('screensTVTopLeft')[0],
				topRight: this._geometry.getItems('screensTVTopRight')[0],
				bottomLeft: this._geometry.getItems('screensTVBottomLeft')[0],
				bottomRight: this._geometry.getItems('screensTVBottomRight')[0]
			},
			this._geometry.getItems('systems-block').concat(this._geometry.getItems('systems-block-move'))
		);
	}

	/**
	 * DepartmentSwitcher._switchScene + HomeField.setActiveSceneProcessor,
	 * forked: stop the outgoing scene synchronously, start the incoming one
	 * a 10ms hop later (the reference's DelayHelper beat — the
	 * skipNormalParticleUpdates one-frame skip is an object-pipeline hack
	 * with no SoA equivalent). Scenes whose departments don't exist yet
	 * resolve to "nothing active" in the pending consumer; their demo pools
	 * are facade-driven and unaffected.
	 */
	private _switchScene(scene: string): void {
		//DepartmentSwitcher._switchScene's event surface (HomeController's
		//drag-prompt reset + work-department tracking listen; the analytics
		//carry the per-scene start counts):
		this._sceneStartCounts[scene]++;
		Simple.event('start_home_scene', { scene: scene, scene_count: this._sceneStartCounts[scene] });

		if (this._activeDepartment) {
			this._activeDepartment.stopAsActiveScene();
		}
		this._activeDepartment = null;
		this._activeSceneName = scene;

		Events.dispatch('CHANGE_HOME_SCENE', { scene: scene });

		this._startupTimeouts.push(
			setTimeout(() => {
				this._pendingSceneStart = scene;
			}, 10)
		);
	}

}
